import { useEffect, useState } from 'react';
import { Divider, Hidden, Grid, Drawer, Box, IconButton, Button, Icon, Menu as MuiMenu } from '@mui/material';
import { AppBar } from '@carto/react-ui';
import DrawerMenu from './DrawerMenu';
import NavigationMenu from './NavigationMenu';
import UserMenu from './UserMenu';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import calLogo from 'assets/img/cal_header_logo.svg';
import MenuItem from '@mui/material/MenuItem';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {
  Menu,
  Close,
  FolderOutlined,
  ChevronRightOutlined,
  KeyboardArrowDownOutlined,
  ViewListOutlined,
  HelpOutlineOutlined,
  PeopleOutlineOutlined,
  PlayCircleOutline,
  Feedback,
  NewReleases,
} from '@mui/icons-material';
import { FeedbackModal } from 'components/views/main/common/FeedbackModal';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import { ROUTE_PATHS } from 'routes';
import { useAppState } from 'components/contexts/State';
import { downloadReportFromGcs } from 'components/views/main/projects/Services/ProjectService';
import FeedbackToasterModal from 'components/views/main/common/FeedbackToasterModal';

export const StyledAppBar = styled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: '#fff',
  boxShadow:
    '0px 1px 14px 0px rgba(0, 0, 0, 0.12), 0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 3px 5px -1px rgba(0, 0, 0, 0.20)',
  height: '56px',
  padding: '1px 16px',
  top: 0,
  '.MuiToolbar-root': {
    padding: '0px',
    height: '100%',
  },
}));
const StyledMenu = styled((props) => (
  <MuiMenu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: 'rgb(55, 65, 81)',
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
        ...theme.applyStyles('dark', {
          color: 'inherit',
        }),
      },
      '&:active': {
        backgroundColor: 'grey'
      },
    },
    ...theme.applyStyles('dark', {
      color: theme.palette.grey[300],
    }),
  },
}));

export const StyledDivider = styled(Divider)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));

export default function Header() {
  const { userRoleDetails } = useAppState();

  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [menuDisabled, setMenuDisabled] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElManual, setAnchorElManual] = useState(null);
  const [anchorElTrainingVideoManual, setAnchorElTrainingVideoManual] = useState(null);
  const open = Boolean(anchorEl);
  const openVideoOptions = Boolean(anchorElTrainingVideoManual);
  const openManualOptions = Boolean(anchorElManual);
  const idVideo = openVideoOptions ? 'simple-popover' : undefined;
  const idManual = openManualOptions ? 'simple-popover' : undefined;
  const {userRole} = useAppState();


  const TRAINING_MANUAL_URI = 'gs://casc-dot-vruai-landing/Caltrans_VRU_Testing_Manual.pdf';
  const WORKZONE_SAFETY_URI = 'gs://casc-dot-vruai-landing/WZS_Training_Manual.pdf'
  const AI_ENABLED_PROJECT_DELIVERY_URI = 'gs://casc-dot-vruai-landing/AEPD_Project_Initiation_Training Manual.pdf'
  const GENERAL_DEMO_URI = 'gs://casc-dot-vruai-landing/VRU_Safety_Platform_General_Elements_Demo.mp4';
  const HQ_SAFETY_DEMO_URI = 'gs://casc-dot-vruai-landing/VRU_Safety_Platform_HQ_Safety_Demo.mp4';
  const DISTRICT_SAFETY_DEMO_URI = 'gs://casc-dot-vruai-landing/VRU_Safety_Platform_District_Safety_Demo.mp4';
  const TRANSIT_DEMO_URI = 'gs://casc-dot-vruai-landing/VRU_Safety_Platform_Transit_Demo.mp4';
  const APED_CAR_PIR_KICKOFF_VIDEO_URI = 'gs://casc-dot-vruai-landing/AEPD_CAR_and_PIR_UAT_Kickoff_and_Training.mp4';
  const WORK_ZONE_SAFETY_KICKOFF_VIDEO_URI = 'gs://casc-dot-vruai-landing/WZS_UAT_Kickoff_and_Training.mp4';
  const AEPD_COST_ESTIMATE_KICKOFF_VIDEO_URI = 'gs://casc-dot-vruai-landing/AEPD_Cost_Estimation_UAT_Kickoff_and_Training.mp4';

  const getBlobFromDownloadPayload = (payload) => {
    if (payload instanceof Blob) {
      return payload;
    }

    if (payload?.blob instanceof Blob) {
      return payload.blob;
    }

    throw new TypeError('Invalid download payload: expected Blob or { blob: Blob }.');
  };
  
  const handleTrainingManualDownload = async (tool) => {
    try {
      if(tool === 'trainingManual'){
      const payload = await downloadReportFromGcs(TRAINING_MANUAL_URI);
      const blob = getBlobFromDownloadPayload(payload);
      const url = URL.createObjectURL(blob);
      const pdfWindow = window.open(url, '_blank');
      if (!pdfWindow) {
        throw new Error('Popup blocked while opening training manual.');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
      } else if(tool === 'workzoneSafety'){
        const payload = await downloadReportFromGcs(WORKZONE_SAFETY_URI);
        const blob = getBlobFromDownloadPayload(payload);
        const url = URL.createObjectURL(blob);
        const pdfWindow = window.open(url, '_blank');
        if (!pdfWindow) {
          throw new Error('Popup blocked while opening work zone safety manual.');
        }
        setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
      } else if(tool === 'aiEnabledProjectDelivery'){
        const payload = await downloadReportFromGcs(AI_ENABLED_PROJECT_DELIVERY_URI);
        const blob = getBlobFromDownloadPayload(payload);
        const url = URL.createObjectURL(blob);
        const pdfWindow = window.open(url, '_blank');
        if (!pdfWindow) {
          throw new Error('Popup blocked while opening AEPD training manual.');
        }
        setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
      } else if(tool === 'transitDemo'){
        const payload = await downloadReportFromGcs(TRANSIT_DEMO_URI);
        const blob = getBlobFromDownloadPayload(payload);
        const url = URL.createObjectURL(blob);
        const pdfWindow = window.open(url, '_blank');
        if (!pdfWindow) {
          throw new Error('Popup blocked while opening transit demo video.');
        }
        setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
      }
    } catch (error) {
      console.error('Error downloading training manual:', error);
    }
  };

  const handleTrainingVideoDownload = async (URI) => {
    try {
      const payload = await downloadReportFromGcs(URI);
      const blob = getBlobFromDownloadPayload(payload);
      const signedUrl = await blob.text();
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Error downloading training video:', error);
    }
  }
  
   const handleTrainingManualOptionClick = (event) => {
   setAnchorElManual(event.currentTarget);
  }

  const handleTrainingVideoOptionClick = (event) => {
    setAnchorElTrainingVideoManual(event.currentTarget);
  }
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setAnchorElManual(null);
    setAnchorElTrainingVideoManual(null);
    setMenuOpen(false);
    setDrawerOpen(false);
  };

  const closeAllMenus = () => {
    setMenuOpen(false);
    setDrawerOpen(false);
    setAnchorEl(null);
    setAnchorElManual(null);
    setAnchorElTrainingVideoManual(null);
  };

  const handleSupportMenuNavigate = (event, path) => {
    event.preventDefault();
    event.stopPropagation();
    closeAllMenus();
    setTimeout(() => navigate(path), 0);
  };

  useEffect(() => {
    closeAllMenus();
  }, [location]);


  useEffect(() => {
    setMenuDisabled(true);
    const timer = setTimeout(() => {
      setMenuDisabled(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const toggleMenu = (newOpen) => () => {
    setMenuOpen(newOpen);
  };

  const [sidebarItems, setSidebarItems] = useState([]);

  function SupportMenu() {

  }
  useEffect(() => {
    const permission = userRole || localStorage.getItem('userRole');

    const items = [];
    // All users can see 'View data'
    items.push([
            {
              text: 'View data',
              icon: (
                <ViewListOutlined
                  sx={{
                    color: '#002547',
                    height: '24px !important',
                    width: '24px !important',
                  }}
                />
              ),
              action: () => {
                navigate(ROUTE_PATHS.DATA);
              },
              activePath: ROUTE_PATHS.DATA,
            },
          ]);
    if (
      permission === 'admin'
    ) {
       
    items.push([
            // {
            //   text: 'Upload data',
            //   icon: (
            //     <UploadOutlined
            //       sx={{
            //         color: '#002547',
            //         height: '24px !important',
            //         width: '24px !important',
            //       }}
            //     />
            //   ),
            //   action: () => {},
            // },
            {
              text: 'Users',
              icon: (
                <PeopleOutlineOutlined
                  sx={{
                    color: '#002547',
                    height: '24px !important',
                    width: '24px !important',
                  }}
                />
              ),
              action: () => {
                navigate(ROUTE_PATHS.USERS);
              },
              activePath: ROUTE_PATHS.USERS,
            },
            
          ]);
          items.push([ {
              text: 'Feedback',
              icon: (
                <Feedback
                  sx={{
                    color: '#002547',
                    height: '24px !important',
                    width: '24px !important',
                  }}
                />
              ),
              action: () => {
                navigate(ROUTE_PATHS.FEEDBACK);
              },
              activePath: ROUTE_PATHS.FEEDBACK,
            }]);
           
    }
    // if (
    //   userRoleDetails &&
    //   userRoleDetails.resources &&
    //   userRoleDetails.resources.includes('manage_urbansim')
    // ) {
    //   items.push([
    //     {
    //       text: 'Switch to Urbansim',
    //       icon: (
    //         <CustomUrbanSimIcon
    //           sx={{
    //             color: '#002547',
    //             height: '24px !important',
    //             width: '24px !important',
    //           }}
    //         />
    //       ),
    //       action: () => {},
    //     },
    //   ]);
    // }
    // All users can see 'Release Notes'
    items.push([ {
              text: 'Release Notes',
              icon: (
                <NewReleases
                  sx={{
                    color: '#002547',
                    height: '24px !important',
                    width: '24px !important',
                  }}
                />
              ),
              action: () => {
                navigate(ROUTE_PATHS.RELEASE_NOTES);
              },
              activePath: ROUTE_PATHS.RELEASE_NOTES,
            }]);
    setSidebarItems(items);
  }, [userRoleDetails,userRole]);

  const DrawerList = (
    <Box sx={{ width: 250 }} role='presentation'>
      <div
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px 16px',
        }}
      >
        <img
          src={calLogo}
          alt='Infra'
          style={{ height: '13.54px', cursor: 'pointer' }}
          onClick={() => {
            toggleMenu(false);
            navigate(ROUTE_PATHS.HOME);
          }}
        />
        <Close
          sx={{
            color: '#002547',
            fontSize: '12px',
            height: '24px !important',
            width: '24px !important',
            cursor: 'pointer',
          }}
          onClick={toggleMenu(false)}
        ></Close>
      </div>
      <List>
        {sidebarItems.map((menuItemList, index) => (
          <>
            {index !== 0 && <Divider sx={{ margin: '1px 0px' }} />}
            {menuItemList.map((menuItem) => (
              <ListItem
                key={menuItem.text}
                disablePadding
                sx={{
                  transition: 'all 0.15s ease-in-out',
                  background:
                    location &&
                    menuItem.activePath &&
                    location.pathname === menuItem.activePath
                      ? 'rgba(33, 150, 243, 0.08)'
                      : 'white',
                  '&:hover': {
                    background:
                      location &&
                      menuItem.activePath &&
                      location.pathname === menuItem.activePath
                        ? 'rgba(33, 150, 243, 0.08)'
                        : 'rgba(0, 37, 71, 0.04)',
                  },
                }}
                onClick={() => {
                  closeAllMenus();
                  if (menuItem.action) {
                    setTimeout(() => {
                      menuItem.action();
                    }, 0);
                  }
                }}
              >
                <ListItemButton
                  sx={{
                    backgroundColor: 'transparent',
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '48px' }}>{menuItem.icon}</ListItemIcon>
                  <ListItemText
                    primary={menuItem.text}
                    sx={{
                      '.MuiTypography-root': {
                        color: 'rgba(0, 37, 71, 0.87) !important',
                        fontSize: '16px',
                        fontWeight: '400',
                        lineHeight: '24px',
                        letterSpacing: '0.15px',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </>
        ))}
      </List>
    </Box>
  );

  return (
    <StyledAppBar
      component='header'
      position='sticky'
      onClickMenu={handleDrawerToggle}
      brandLogo={
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {sidebarItems && sidebarItems.length > 0 && (
            <Menu
              sx={{
                color: menuDisabled ? '#bdbdbd' : '#002547',
                fontSize: '16px',
                height: '32px !important',
                width: '32px !important',
                cursor: menuDisabled ? 'not-allowed' : 'pointer',
                pointerEvents: menuDisabled ? 'none' : 'auto',
              }}
              onClick={menuDisabled ? undefined : toggleMenu(true)}
            ></Menu>
          )}
          <img
            src={calLogo}
            alt='Infra'
            style={{ height: '13.54px', cursor: 'pointer' }}
            onClick={() => {
              toggleMenu(false);
              navigate(ROUTE_PATHS.HOME);
            }}
          />
        </div>
      }
      sx={{ '.MuiToolbar-regular img': { width: 'auto' } }}
    >
      <Hidden mdUp>
        <DrawerMenu drawerOpen={drawerOpen} handleDrawerToggle={handleDrawerToggle} />
      </Hidden>

      <StyledDivider orientation='vertical' flexItem light></StyledDivider>

      <NavigationMenu />
      <Button
        variant='contained'
        color='primary'
        size='medium'
        sx={{
          margin: '0 16px',
          width: '180px',
          minWidth: '180px',
          maxWidth: '180px',
          textTransform: 'none',
          padding: '4px 10px',
          fontSize: '13px',
          fontWeight: '500',
          whiteSpace: 'nowrap',
          lineHeight: '13px',
          display: 'flex',
          gap: '8px',
          flexShrink: 0,
        }}
        startIcon={<AnnouncementOutlinedIcon /> }
        onClick={() => {
          setShowFeedbackModal(true);
        }}
        >Provide Feedback</Button>
        <FeedbackModal
        open={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        title='Please provide feedback'
        onSubmit={() => {
          setShowFeedbackModal(false);
          // Handle feedback submission logic here
        }}
      />  
      <FeedbackToasterModal />
  <div>
        <Button
          id="demo-customized-button"
          aria-controls={open ? 'demo-customized-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          variant="contained"
          disableElevation
          onClick={handleClick}
          endIcon={<KeyboardArrowDownOutlined />}
          sx={{
            marginRight: '12px',
            backgroundColor: '#e0e0e0', 
            color: '#333333', 
            borderRadius: '25px', 
            padding: '8px 16px',
            border: '1px solid #d0d0d0', 
            '&:hover': {
              backgroundColor: '#d5d5d5', 
            },
            '&:active': {
              backgroundColor: '#cccccc', 
            },
            textTransform: 'none', 
          }}
        >
          Support
        </Button>
        <StyledMenu
          id="demo-customized-menu"
          slotProps={{
            list: {
              'aria-labelledby': 'demo-customized-button',
            },
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuItem onClick={handleTrainingVideoOptionClick}  disableRipple>
            <PlayCircleOutline />
            Training Video
            <ChevronRightOutlined sx={{ fontSize: '16px', color: 'rgba(0, 0, 0, 0.6)', marginRight: 'auto' }} />
          </MenuItem>
          <StyledMenu
                  id={idVideo}
                  open={openVideoOptions}
                  anchorEl={anchorElTrainingVideoManual}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                <MenuItem onClick={(e) => {
                  e.stopPropagation();
                 handleTrainingVideoDownload(GENERAL_DEMO_URI);
                 handleClose();
                }} >
                  Road Safety Insights Platform General Elements
                </MenuItem>
                <MenuItem onClick={(e) => {
                  e.stopPropagation();
                 handleTrainingVideoDownload(HQ_SAFETY_DEMO_URI);
                 handleClose();
                }} >
                 Core WorkFlow for Caltrans HQ Safety Persona
                </MenuItem>
                <MenuItem onClick={(e) => {
                  e.stopPropagation();
                 handleTrainingVideoDownload(DISTRICT_SAFETY_DEMO_URI);
                 handleClose();
                }} >
                   Core WorkFlow for Caltrans District Safety Persona
                </MenuItem>
                <MenuItem onClick={(e) => {
                  e.stopPropagation();
                 handleTrainingVideoDownload(TRANSIT_DEMO_URI);
                 handleClose();
                }} >
                 Core WorkFlow for Caltrans Transit Persona
                </MenuItem> 
                <MenuItem onClick={(e) => {
                  e.stopPropagation();
                 handleTrainingVideoDownload(TRANSIT_DEMO_URI);
                 handleClose();
                }} >
                 Core WorkFlow for Caltrans Transit Persona
                </MenuItem>
                <MenuItem onClick={(e) => {
                  e.stopPropagation();
                 handleTrainingVideoDownload(APED_CAR_PIR_KICKOFF_VIDEO_URI);
                 handleClose();
                }} >
                 AEPD CAR and PIR Kickoff Video
                </MenuItem>
                 <MenuItem onClick={(e) => {
                  e.stopPropagation();
                 handleTrainingVideoDownload(WORK_ZONE_SAFETY_KICKOFF_VIDEO_URI);
                 handleClose();
                }} >
                  Work Zone Safety Kickoff Video
                </MenuItem>
                <MenuItem onClick={(e) => {
                  e.stopPropagation();
                 handleTrainingVideoDownload(AEPD_COST_ESTIMATE_KICKOFF_VIDEO_URI);
                 handleClose();
                }} >
                 AEPD Cost Estimate Kickoff Video
                </MenuItem>
                </StyledMenu>
           
          <MenuItem onClick={handleTrainingManualOptionClick} disableRipple>
            <HelpOutlineOutlined /> 
            Training Manual
                        <ChevronRightOutlined sx={{ fontSize: '16px', color: 'rgba(0, 0, 0, 0.6)', marginRight: 'auto' }} />

          </MenuItem>
          
           <StyledMenu
                  id={idManual}
                  open={openManualOptions}
                  anchorEl={anchorElManual}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                <MenuItem onClick={(e) => {
                  e.stopPropagation();
                 handleTrainingManualDownload("trainingManual");
                 handleClose();
                }} >
                 Safety Training Manual 
                </MenuItem>
                <MenuItem onClick={(e) => {
                  e.stopPropagation();
                 handleTrainingManualDownload("workzoneSafety");
                 handleClose();
                }} >
                 Work Zone Safety Training Manual
                </MenuItem>
                <MenuItem onClick={(e) => {
                  e.stopPropagation();
                 handleTrainingManualDownload("aiEnabledProjectDelivery");
                 handleClose();
                }} >
                 AI Enabled Project Planning Training Manual
                </MenuItem>
               
                </StyledMenu>
           
          {(userRoleDetails &&
            userRoleDetails.resources &&
            (userRoleDetails.resources.includes('data_repo_self_view') ||
              userRoleDetails.resources.includes('data_repo_all_view'))
          ) && 
           <MenuItem onClick={(event) => handleSupportMenuNavigate(event, ROUTE_PATHS.DATA)}>
             <ViewListOutlined/>
             View Data
          </MenuItem>
        }
          <MenuItem onClick={(event) => handleSupportMenuNavigate(event, ROUTE_PATHS.RELEASE_NOTES)}>
             <NewReleases/>
             Release Notes
          </MenuItem>
        </StyledMenu>
      </div>

      {/* <HelpOutlineOutlined
        sx={{
          color: '#002547',
          fontSize: '24px',
          cursor: 'pointer',
          marginRight: '16px',
        }}
        onClick={() => {
          //Download the PDF
             handleTrainingManualDownload();
        }}
        onMouseEnter={(e) => {
            e.target.style.color = '#1976d2';
            const hoverDiv = document.createElement('div');
            hoverDiv.textContent = 'Download Training Manual';
            hoverDiv.style.position = 'absolute';
            hoverDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
            hoverDiv.style.color = '#fff';
            hoverDiv.style.padding = '4px 8px';
            hoverDiv.style.borderRadius = '4px';
            hoverDiv.style.fontSize = '12px';
            hoverDiv.style.maxWidth = '200px';
            hoverDiv.style.whiteSpace = 'nowrap';
            hoverDiv.style.top = `${e.clientY + 10}px`;
            hoverDiv.style.left = `${e.clientX - 100}px`;
            hoverDiv.style.zIndex = '9999';
            hoverDiv.setAttribute('id', 'hoverDiv');
            document.body.appendChild(hoverDiv);
        }}  
        onMouseLeave={(e) => {
        const hoverDiv = document.getElementById('hoverDiv');
        if (hoverDiv) {
          document.body.removeChild(hoverDiv);
        }
        e.target.style.color = '#002547';
      }}
      ></HelpOutlineOutlined> */}
      <Grid container item xs justifyContent='flex-end'>
        <UserMenu />
      </Grid>

      {sidebarItems && sidebarItems.length > 0 && (
        <Drawer
          open={menuOpen}
          onClose={toggleMenu(false)}
          sx={{
            zIndex: '1350',
            '.MuiPaper-root:focus-visible': {
              outline: 'none !important',
              boxShadow: 'none !important',
              borderRadius: '0px',
            },
            '.MuiBox-root': {
              width: '320px',
            },
          }}
        >
          {DrawerList}
        </Drawer>
      )}
    </StyledAppBar>
  );
}
